import { useMemo } from 'react';
import { useAppSelector } from '@store/index';
import { selectChatOpen, selectChatWidth, selectOverlayPanel, selectSidebarMode } from '@features/ui/uiSlice';
import {
  CHAT_DOCK_BREAKPOINT,
  CHAT_PANEL,
  LAYOUT,
  MOBILE_BREAKPOINT,
} from '@constants/layout';
import { useBreakpointBelow, useBreakpointUp, useMediaQuery } from './useMediaQuery';

export interface ShellLayout {
  isMobile: boolean;
  railWidth: number;
  isRailExpanded: boolean;
  navDrawerOpen: boolean;
  chatVisible: boolean;
  /** This width docks the panel — independent of whether it is currently open. */
  chatDocked: boolean;
  /** Docked *and* open, i.e. the panel is actually taking a grid column. */
  hasChatColumn: boolean;
  chatWidth: number;
  /** Width the chat column contributes; zero whenever the panel floats. */
  chatColumnWidth: number;
  contentMaxWidth: number | undefined;
}

/**
 * Effect-free by design. An earlier version dispatched on resize to force mobile
 * state, which fought the user's own toggles and made the drawer unopenable.
 */
export function useShellLayout(): ShellLayout {
  const sidebarMode = useAppSelector(selectSidebarMode);
  const overlayPanel = useAppSelector(selectOverlayPanel);
  const chatOpen = useAppSelector(selectChatOpen);
  const storedChatWidth = useAppSelector(selectChatWidth);

  const isMobile = useBreakpointBelow(MOBILE_BREAKPOINT);
  const chatDocked = useBreakpointUp(CHAT_DOCK_BREAKPOINT);
  const isWide = useBreakpointUp('2xl');
  const isUltraWide = useMediaQuery(`(min-width: ${LAYOUT.ULTRA_WIDE_MIN}px)`);

  return useMemo(() => {
    const isRailExpanded = sidebarMode === 'expanded';

    // Only a docked panel honours the persisted flag; floating, it opens on
    // request, or a remembered "open" covers the page on load.
    const chatVisible = chatDocked ? chatOpen : overlayPanel === 'chat';
    const isChatColumn = chatVisible && chatDocked;

    // Likewise the dragged width, or a stored 800px floats across a tablet.
    const defaultChatWidth = isWide
      ? CHAT_PANEL.WIDTH_BY_BREAKPOINT['2xl']
      : CHAT_PANEL.WIDTH_BY_BREAKPOINT.xl;
    const chatWidth = (chatDocked && storedChatWidth) || defaultChatWidth;

    return {
      isMobile,
      railWidth: isMobile ? 0 : isRailExpanded ? LAYOUT.SIDEBAR_WIDTH_EXPANDED : LAYOUT.SIDEBAR_WIDTH_MINI,
      isRailExpanded,
      navDrawerOpen: isMobile && overlayPanel === 'nav',
      chatVisible,
      chatDocked,
      hasChatColumn: isChatColumn,
      chatWidth,
      chatColumnWidth: isChatColumn ? chatWidth : 0,
      contentMaxWidth: isUltraWide ? LAYOUT.MAIN_CONTENT_MAX_WIDTH : undefined,
    };
  }, [sidebarMode, overlayPanel, chatOpen, storedChatWidth, isMobile, chatDocked, isWide, isUltraWide]);
}
